import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader,
  Alert,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Paper,
  Center,
} from "@mantine/core";
import { IconArrowLeft, IconAlertCircle } from "@tabler/icons-react";
import {
  useSessionDownload,
  RecordingPlayer,
} from "@deep-consulting-solutions/session-recording-s3";
import dayjs from "dayjs";

interface DeepLinkSessionPlayerProps {
  sessionId: string;
  userId: string;
  date: string;
}

export default function DeepLinkSessionPlayer({
  sessionId,
  userId,
  date,
}: DeepLinkSessionPlayerProps) {
  const navigate = useNavigate();
  const { events, metadata, loading, error } = useSessionDownload(
    sessionId,
    userId,
    date
  );

  const handleBack = useCallback(() => {
    navigate("/admin/session-recordings");
  }, [navigate]);

  if (loading) {
    return (
      <Center h="100%">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="lg" fw={500}>
            Loading session recording...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100%">
        <Stack align="center" gap="md" maw={500}>
          <Alert
            color="red"
            title="Could not load session recording"
            icon={<IconAlertCircle size={16} />}
          >
            {error}
          </Alert>
          <Button
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            onClick={handleBack}
          >
            Back to all recordings
          </Button>
        </Stack>
      </Center>
    );
  }

  if (events.length === 0) {
    return (
      <Center h="100%">
        <Stack align="center" gap="md" maw={500}>
          <Alert
            color="yellow"
            title="No recording found"
            icon={<IconAlertCircle size={16} />}
          >
            No recording found for that session.
          </Alert>
          <Button
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            onClick={handleBack}
          >
            Back to all recordings
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap={0} h="100%" style={{ overflow: "hidden" }}>
      <Paper p="md" withBorder radius={0}>
        <Group justify="space-between" align="center">
          <Stack gap={4}>
            <Title order={4}>Session Recording</Title>
            <Group gap="xl">
              <Text size="sm" c="dimmed">
                ID: {sessionId}
              </Text>
              {metadata?.startedAt && (
                <Text size="sm" c="dimmed">
                  Started:{" "}
                  {dayjs(metadata.startedAt).format("MMM D, YYYY h:mm A")}
                </Text>
              )}
              {(metadata?.userName || metadata?.userEmail) && (
                <Text size="sm" c="dimmed">
                  User: {metadata.userName || metadata.userEmail}
                  {metadata.userName && metadata.userEmail
                    ? ` (${metadata.userEmail})`
                    : ""}
                </Text>
              )}
            </Group>
          </Stack>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={handleBack}
          >
            Back to all recordings
          </Button>
        </Group>
      </Paper>
      <div style={{ flex: 1, position: "relative", backgroundColor: "black" }}>
        <RecordingPlayer
          events={events}
          autoPlay
          showController
          style={{ height: "100%", width: "100%" }}
        />
      </div>
    </Stack>
  );
}
